import { ActionType, AppConstants, HttpErrorMessage } from '@aston/foundation';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Injectable, inject } from '@angular/core'
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';

import { DunningActionSendingCostsModalComponent } from '../../../dunnings-module/components/dunning-action-postal-costs-modal/dunning-action-postal-costs-modal.component';
import { DunningActionsService } from '../../../dunnings-module/services';
import { useApiMessageException } from '../../../shared-module/functions';
import * as AppStoreActions from '../../app-store/actions';
import * as featureActions from '../actions';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreSendingCostsEffects {

	private modalService = inject(NgbModal);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private dunningActionsService = inject(DunningActionsService);

	checkSendingCostsBalanceRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CheckSendingCostsBalanceRequest),
		switchMap(action => this.dunningActionsService.checkSendingCosts(action.form).pipe(
			map(entity => featureActions.CheckSendingCostsBalanceSuccess({ entity })),
			catchError((error: HttpErrorMessage) => {
				error = useApiMessageException(error, 'PostalMailCannotBeSent');
				return of(featureActions.CheckSendingCostsBalanceFailure({error}));
			})
		))
	));

	checkSendingCostsBalanceModal$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CheckSendingCostsBalanceRequest),
		map(action => {
			const modalRef = this.modalService.open(DunningActionSendingCostsModalComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, modalDialogClass: 'ModalDialog-CheckSendingCostsBalance' });
			const modalInstance: DunningActionSendingCostsModalComponent = modalRef.componentInstance;
			modalInstance.resultEvent.pipe(
				catchError(reason => {
					this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: reason}));
					this.store.dispatch(featureActions.CancelSendingCostsBalanceRequest(action.form));
					return EMPTY;
				}),
				map(event => {
					const reason = event.type;
					if (reason === ActionType.SUBMIT) {
						this.store.dispatch(featureActions.ConfirmSendingCostsBalanceRequest(action.form));
						return;
					}

					modalRef.dismiss();
					this.store.dispatch(featureActions.CancelSendingCostsBalanceRequest(action.form));
				})
			).subscribe();
			return AppStoreActions.Noop();
		})
	));

	confirmSendingCostsRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ConfirmSendingCostsBalanceRequest),
		switchMap(action => this.dunningActionsService.confirmSendingCosts(action.form).pipe(
			switchMap(entity => {
				this.modalService.dismissAll()
				return [
					featureActions.ConfirmSendingCostsBalanceSuccess({ entity }),
					featureActions.CompleteDunningActionSuccess({ entity: action.form })
				]
			}),
			catchError(error => {
				this.modalService.dismissAll()
				error = useApiMessageException(error, 'Aggregate');
				const modalRef = this.modalService.open(DunningActionSendingCostsModalComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, modalDialogClass: 'ModalDialog-ConfirmSendingCostsBalance' });
				const component = modalRef.componentInstance as DunningActionSendingCostsModalComponent;
				component.resultEvent.subscribe(() => modalRef.dismiss());

				return of(featureActions.ConfirmSendingCostsBalanceFailure({ error }));
			}),
		))
	));

	cancelSendingCostsRequest$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CancelSendingCostsBalanceRequest),
		switchMap(action => this.dunningActionsService.cancelSendingCosts(action.form).pipe(
			switchMap(entity => [
				featureActions.CancelSendingCostsBalanceSuccess({ entity }),
				AppStoreActions.ToastWarning('Dunning.Actions.PostalSendingCanceled'),
			]),
			catchError(error => of(featureActions.CancelSendingCostsBalanceFailure({ error }))),
		))
	));
}
